import React, { useState, useEffect } from "react";
import styles from "./EnterpriseModal.module.css";
import { CloseIcon } from "./Icons";
import { db } from "config/firebase";
import { collection, addDoc } from "firebase/firestore";

interface EnterpriseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EnterpriseModal: React.FC<EnterpriseModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [formStep, setFormStep] = useState<"form" | "success">("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    role: "",
  });

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormStep("form");
        setFormData({ fullName: "", email: "", company: "", role: "" });
        setIsSubmitting(false);
      }, 300);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "enterprise_requests"), {
        ...formData,
        createdAt: new Date(),
      });
      setFormStep("success");
    } catch (error) {
      console.error("Error submitting form: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${styles.modalOverlay} ${isOpen ? styles.active : ""}`}>
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          <CloseIcon />
        </button>
        <div className={styles.modalHeader}>
          {formStep === "form" ? (
            <h2 id="modal-title" className={styles.modalTitle}>
              Build with us
            </h2>
          ) : (
            <div />
          )}
        </div>

        {formStep === "form" ? (
          <>
            <p className={styles.modalDesc}>
              MindScriber is opening selected access for enterprise teams
              building the next generation of AI systems. Share your details and
              our team will review where physiological intelligence can create
              the strongest deployment path.
            </p>

            <form onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <input
                    id="fullName"
                    className={styles.input}
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Full name*"
                    title="Enter your full name"
                    disabled={isSubmitting}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <input
                    id="company"
                    className={styles.input}
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Company*"
                    title="Enter your company name"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <input
                    id="email"
                    className={styles.input}
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Work email*"
                    title="Enter your work email address"
                    disabled={isSubmitting}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <input
                    id="role"
                    className={styles.input}
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleInputChange}
                    placeholder="Role*"
                    title="Enter your job role"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className={styles.submitRow}>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isSubmitting}
                  style={{ opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? "Sending..." : "Request"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className={styles.successMessage}>
            <h3 id="modal-title">Thank you.</h3>
            <p>
              Our team will review it and get back if there is a strong fit for
              the next Scriber O enterprise opening.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
